import { createReducer, on } from '@ngrx/store';

import {
  activateFactor,
  clearActivateFactor,
  exchangeToken,
  resendCodeToFactor,
  sendCodeToFactor,
  signIn,
  signInFailed,
  signInSuccess,
  updateTransaction,
  verifyFactor,
  onNewRegistration,
  resetState,
  enrollEmailFactor,
  enrollPhoneFactor,
  skipEnrollFactor,
  impersonateSuccess,
  updateLoginClient,
  introspectSuccess,
  loginTimeout,
  updateUserAgreementVersion,
  toggleLoadingSpinner,
  scheduleLoadingSpinnerWithDelay,
  disableLoadingSpinner,
  addTokenText,
  addStructIdLevel3,
  resetPasswordFailed,
  resetPassword,
  mfaTimeOut,
  clearSignInError,
  preLoadProfile,
  preLoadProfileSuccess,
  setLockoutLoginRedirectUrl,
} from './login.actions';
import {
  SignInFailReason,
  AuthTransaction,
  LoginStatus,
  LoginClient,
  ResetPasswordFailReason,
} from './login.models';

export const LOGIN_FEATURE_KEY = 'login';

export interface LoginState {
  transaction?: AuthTransaction;
  hasTransaction: boolean;
  hasBeenSignedIn: boolean;
  signInError?: SignInFailReason;
  isTransactionInProgress: boolean;
  hasValidCredentials: boolean;
  canGoBack?: boolean;
  registrationPhoneNumber?: string;
  newPhoneNumber?: string;
  isANewUser?: boolean;
  shouldSkipMfaWelcomeScreen?: boolean;
  isAfterRegistration: boolean;
  eapToken?: string;
  clientId: LoginClient;
  stepupToken?: string;
  userAgreementVersion?: string;
  spinnerCount: number;
  isSpinnerShown?: boolean;
  isProfileLoading?: boolean;
  tokenText?: string;
  structIdLevel3?: string;
  resetPasswordError?: ResetPasswordFailReason;
  lockOutLoginRedirectUrl?: string;
}

export interface LoginPartialState {
  readonly [LOGIN_FEATURE_KEY]: LoginState;
}

export const initialState: LoginState = {
  hasBeenSignedIn: false,
  isTransactionInProgress: false,
  hasTransaction: false,
  hasValidCredentials: false,
  isAfterRegistration: false,
  clientId: LoginClient.CignaForBrokers,
  spinnerCount: 0,
};

export const loginReducer = createReducer(
  initialState,
  on(updateTransaction, (state, { transaction }) => {
    if (!transaction.status) {
      return state;
    }
    return {
      ...state,
      transaction,
      isTransactionInProgress: false,
      hasTransaction: true,
      hasBeenSignedIn: false,
      signInError: undefined,
      hasValidCredentials:
        state.hasValidCredentials ||
        [
          LoginStatus.SUCCESS,
          LoginStatus.MFA_REQUIRED,
          LoginStatus.MFA_ENROLL,
        ].some((v) => v === transaction.status),
    };
  }),
  on(mfaTimeOut, (state) => ({
    ...state,
    signInError: undefined,
    isTransactionInProgress: false,
    hasTransaction: state.hasTransaction ? true : false,
    isSpinnerShown: false,
    spinnerCount: 0,
  })),
  on(signInFailed, (state, { reason }) => ({
    ...state,
    signInError: reason,
    isTransactionInProgress: false,
    hasTransaction: state.hasTransaction
      ? reason !== SignInFailReason.LockedOut // prevents cancel to be called after lockout
      : false,
    isSpinnerShown: false,
    spinnerCount: 0,
  })),
  on(signInSuccess, (state) => ({
    ...initialState,
    isAfterRegistration: state.isAfterRegistration,
    hasBeenSignedIn: true,
    factorVerification: false,
    isANewUser: state.isANewUser,
    eapToken: state.eapToken,
    clientId: state.clientId,
    isSpinnerShown: state.isSpinnerShown,
    spinnerCount: state.spinnerCount,
  })),
  on(signIn, (state, { options }) => ({
    ...state,
    isTransactionInProgress: true,
    eapToken: options.tokenText,
  })),
  on(sendCodeToFactor, (state, { canGoBack }) => ({
    ...state,
    canGoBack,
    isTransactionInProgress: true,
  })),
  on(resendCodeToFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
  })),
  on(verifyFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
    factorVerification: true,
    signInError: undefined,
  })),
  on(exchangeToken, (state) => ({
    ...state,
    isTransactionInProgress: true,
  })),
  on(enrollEmailFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
    isANewUser: true,
    signInError: undefined,
  })),
  on(enrollPhoneFactor, (state, { phoneNumber }) => ({
    ...state,
    isTransactionInProgress: true,
    newPhoneNumber: phoneNumber,
    signInError: undefined,
  })),
  on(skipEnrollFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
  })),
  on(activateFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
    signInError: undefined,
  })),
  on(clearActivateFactor, (state) => ({
    ...state,
    signInError: undefined,
  })),

  on(onNewRegistration, (state, { phoneNumber }) => ({
    ...state,
    registrationPhoneNumber: phoneNumber,
    shouldSkipMfaWelcomeScreen: true,
    isAfterRegistration: true,
  })),
  on(resetState, () => ({
    ...initialState,
  })),
  on(impersonateSuccess, (state) => ({ ...state, hasBeenSignedIn: true })),
  on(updateLoginClient, (state, { clientId }) => ({ ...state, clientId })),
  on(introspectSuccess, (state, { token }) => ({
    ...state,
    stepupToken: token,
  })),
  on(loginTimeout, (state) => ({ ...state, signInError: undefined })),
  on(updateUserAgreementVersion, (state, { version }) => ({
    ...state,
    userAgreementVersion: version,
  })),
  on(scheduleLoadingSpinnerWithDelay, (state) => ({
    ...state,
    spinnerCount: state.spinnerCount + 1,
  })),
  on(disableLoadingSpinner, (state) => ({
    ...state,
    spinnerCount: 0,
    isSpinnerShown: false,
  })),
  on(toggleLoadingSpinner, (state, { isEnabled }) => ({
    ...state,
    isSpinnerShown: isEnabled,
  })),
  on(addTokenText, (state, { tokenText }) => ({
    ...state,
    tokenText,
  })),
  on(addStructIdLevel3, (state, { structIdLevel3 }) => ({
    ...state,
    structIdLevel3,
  })),
  on(resetPassword, (state, { options }) => ({
    ...state,
    isTransactionInProgress: true,
    resetPasswordError: undefined,
    // eapToken: options.tokenText,
  })),
  on(resetPasswordFailed, (state, { reason }) => ({
    ...state,
    resetPasswordError: reason,
    isTransactionInProgress: false,
    isSpinnerShown: false,
    spinnerCount: 0,
  })),
  on(clearSignInError, (state) => ({ ...state, signInError: undefined })),
  on(preLoadProfile, (state) => ({ ...state, isProfileLoading: true })),
  on(preLoadProfileSuccess, (state) => ({ ...state, isProfileLoading: false })),
  on(setLockoutLoginRedirectUrl, (state, { url }) => ({
    ...state,
    lockOutLoginRedirectUrl: url,
  })),
);
